import { useEffect } from 'react';
import { Header, SpaceBetween } from '@amzn/awsui-components-react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { AppOutletContext } from '../App';
import { ReachOutPanel } from '../components/nonprofits/reachOutPanel';
import { ReachOutTipsHelpPanel } from '../components/nonprofits/reachOutTipsHelpPanel';
import {
  REACH_OUT_MOCK_PARAM,
  areReachOutMocksAllowed,
  getReachOutMock,
} from '../components/nonprofits/mocks/reachOutPanelFixtures';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { useLocalizedText } from '../hooks/localizedText';
import { NAVIGATION_REDESIGN, NON_PROFIT_SEARCH } from '../utils/constants';

const I18N = 'nonProfitProfile';

/**
 * Shell for a non-profit's profile. It renders a heading and, on review stages only, the Reach out
 * panel filled from mock data: nothing is fetched and `causeId` is not read yet.
 *
 * The heading is placeholder copy with no id in the website.nonprofits translation bundle, so it
 * reads English in every locale rather than spending a translation cycle on text due to be replaced:
 * the finished page is headed by the organization's own name, which is data and never translated.
 */
export function NonProfitProfile() {
  const { t } = useLocalizedText(['nonprofits', 'translation']);
  const [searchParams] = useSearchParams();
  const outletContext = useOutletContext<AppOutletContext | null>();
  const reachOutMock = areReachOutMocksAllowed()
    ? getReachOutMock(searchParams.get(REACH_OUT_MOCK_PARAM))
    : null;

  // Leave the tools slot as it was found: it belongs to the shared AppLayout, so a panel left behind
  // would open on whichever page the user goes to next.
  useEffect(() => {
    return () => {
      outletContext?.setTools(null);
      outletContext?.setToolsOpen(false);
    };
  }, [outletContext]);

  return (
    <div data-testid="nonprofit-profile-page">
      <SpaceBetween size="l">
        <Header variant="h1">{t(`${I18N}.title`)}</Header>
        {reachOutMock && (
          <ReachOutPanel
            {...reachOutMock}
            onOpenTips={() => {
              // One-way open, as on the search page: the AppLayout's own close button closes it.
              outletContext?.setTools(<ReachOutTipsHelpPanel />);
              outletContext?.setToolsOpen(true);
            }}
          />
        )}
      </SpaceBetween>
    </div>
  );
}

/** Gated on both flags, for the reasons given on NonProfitSearchView. */
export function NonProfitProfileView() {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  if (
    featureIsEnabled(NON_PROFIT_SEARCH, features, searchParams) &&
    featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams)
  ) {
    return <NonProfitProfile />;
  }
  return <></>;
}
